class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.abortController = new AbortController();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, FACET_DEBOUNCE_MS);

    const facetForm = this.querySelector("form");
    if (facetForm) facetForm.addEventListener("input", this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true, abortController = null) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();

    FacetFiltersForm.showLoadingState();

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event, abortController);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static showLoadingState() {
    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById(
      "ProductCountDesktop"
    );
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner"
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove("hidden"));
    document
      .getElementById("ProductGridContainer")
      ?.querySelector(".collection")
      ?.classList.add("loading");
    if (countContainer) {
      countContainer.classList.add("loading");
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add("loading");
    }
  }

  static renderSectionFromFetch(url, event, signal = null) {
    if (!signal) {
      FacetFiltersForm.abortController.abort();
      FacetFiltersForm.abortController = new AbortController();
    }

    fetch(url, { signal: signal || FacetFiltersForm.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        // PERF-03: Bound filterData cache to prevent unbounded memory growth.
        // Each entry stores full section HTML (~5-50KB). Cap at 20 entries
        // (FIFO eviction) to limit memory to ~1MB worst case.
        if (FacetFiltersForm.filterData.length >= 20) {
          FacetFiltersForm.filterData.shift();
        }
        FacetFiltersForm.filterData.push({ html, url });
        // Parse once and reuse for all render calls
        const parsedHTML = new DOMParser().parseFromString(html, "text/html");
        FacetFiltersForm.renderFilters(parsedHTML, event);
        FacetFiltersForm.renderProductGridContainer(parsedHTML);
        FacetFiltersForm.renderProductCount(parsedHTML);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        console.error('Facet filter fetch failed:', error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    // Parse once and reuse for all render calls
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    FacetFiltersForm.renderFilters(parsedHTML, event);
    FacetFiltersForm.renderProductGridContainer(parsedHTML);
    FacetFiltersForm.renderProductCount(parsedHTML);
  }

  static renderProductGridContainer(parsedHTML) {
    const targetContainer = document.getElementById("ProductGridContainer");
    const sourceContainer = parsedHTML.getElementById("ProductGridContainer");
    if (!targetContainer || !sourceContainer) return;
    targetContainer.innerHTML = sourceContainer.innerHTML;

    targetContainer
      .querySelectorAll(".scroll-trigger")
      .forEach((element) => {
        element.classList.add("scroll-trigger--cancel");
      });
  }

  static renderProductCount(parsedHTML) {
    const sourceCount = parsedHTML.getElementById("ProductCount");
    if (!sourceCount) return;
    const count = sourceCount.innerHTML;
    const container = document.getElementById("ProductCount");
    const containerDesktop = document.getElementById("ProductCountDesktop");
    if (container) {
      container.innerHTML = count;
      container.classList.remove("loading");
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove("loading");
    }
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner"
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add("hidden"));
  }

  static renderFilters(parsedHTML, event) {
    FacetFiltersForm.renderFacetElements(parsedHTML, event);
    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    FacetFiltersForm.renderFacetCounts(parsedHTML, event);
  }

  /**
   * Syncs all facet filter elements (except the one currently being changed)
   * from the new HTML to the DOM, adding/removing as needed.
   * @param {Document} parsedHTML - Parsed new HTML document
   * @param {Event|undefined} event - The triggering event, if any
   */
  static renderFacetElements(parsedHTML, event) {
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter"
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter"
    );

    FacetFiltersForm.removeStaleFilters(
      facetDetailsElementsFromDom,
      facetDetailsElementsFromFetch
    );

    const facetsToRender = FacetFiltersForm.getFilterElementsToRender(
      facetDetailsElementsFromFetch,
      event
    );

    FacetFiltersForm.upsertFilterElements(facetsToRender);
  }

  /**
   * Removes DOM filter elements that no longer exist in the fetched HTML.
   * @param {NodeList} domElements - Current filter elements in the DOM
   * @param {NodeList} fetchedElements - Filter elements from fetched HTML
   */
  static removeStaleFilters(domElements, fetchedElements) {
    Array.from(domElements).forEach((currentElement) => {
      if (
        !Array.from(fetchedElements).some(
          ({ id }) => currentElement.id === id
        )
      ) {
        currentElement.remove();
      }
    });
  }

  /**
   * Returns the list of fetched filter elements to render, excluding the
   * currently-active filter (the one the user is interacting with).
   * @param {NodeList} fetchedElements - Filter elements from fetched HTML
   * @param {Event|undefined} event - The triggering event, if any
   * @returns {Element[]}
   */
  static getFilterElementsToRender(fetchedElements, event) {
    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    return Array.from(fetchedElements).filter(
      (element) => !matchesId(element)
    );
  }

  /**
   * Updates existing filter elements or inserts new ones in the correct position.
   * @param {Element[]} facetsToRender - Filter elements to upsert into the DOM
   */
  static upsertFilterElements(facetsToRender) {
    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML =
          elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } =
            facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document
            .querySelector(`#${elementToRender.parentElement.id} .js-filter`)
            .before(elementToRender);
        }
      }
    });
  }

  /**
   * Renders updated counts for the facet that was just changed,
   * and focuses the appropriate element for accessibility.
   * @param {Document} parsedHTML - Parsed new HTML document
   * @param {Event|undefined} event - The triggering event, if any
   */
  static renderFacetCounts(parsedHTML, event) {
    if (!event) return;

    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter"
    );

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(
      matchesId
    );

    if (countsToRender) {
      const closestJSFilterID = event.target.closest(".js-filter").id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(
          countsToRender,
          event.target.closest(".js-filter")
        );
        FacetFiltersForm.renderMobileCounts(
          countsToRender,
          document.getElementById(closestJSFilterID)
        );

        FacetFiltersForm.focusActiveElement(closestJSFilterID, event);
      }
    }
  }

  /**
   * Focuses the appropriate element after a facet filter update.
   * @param {string} closestJSFilterID - The ID of the updated filter element
   * @param {Event} event - The triggering event
   */
  static focusActiveElement(closestJSFilterID, event) {
    const newFacetDetailsElement = document.getElementById(closestJSFilterID);
    const newElementSelector = newFacetDetailsElement.classList.contains(
      "mobile-facets__details"
    )
      ? `.mobile-facets__close-button`
      : `.facets__summary`;
    const newElementToActivate =
      newFacetDetailsElement.querySelector(newElementSelector);

    const isTextInput = event.target.getAttribute("type") === "text";

    if (newElementToActivate && !isTextInput) newElementToActivate.focus();
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      ".active-facets-mobile",
      ".active-facets-desktop",
    ];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const targetElement = document.querySelector(selector);
      if (!targetElement) return;
      targetElement.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      ".mobile-facets__open",
      ".mobile-facets__count",
      ".sorting",
    ];

    mobileElementSelectors.forEach((selector) => {
      const sourceElement = html.querySelector(selector);
      const targetElement = document.querySelector(selector);
      if (!sourceElement || !targetElement) return;
      targetElement.innerHTML = sourceElement.innerHTML;
    });

    document
      .getElementById("FacetFiltersFormMobile")
      ?.closest("menu-drawer")
      ?.bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector(".facets__summary");
    const sourceSummary = source.querySelector(".facets__summary");

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector(".facets__header");
    const sourceHeaderElement = source.querySelector(".facets__header");

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector(".facets-wrap");
    const sourceWrapElement = source.querySelector(".facets-wrap");

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(
        target.querySelector("show-more-button .label-show-more.hidden")
      );
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll(".facets__item.hidden")
          .forEach((hiddenItem) =>
            hiddenItem.classList.replace("hidden", "show-more-item")
          );
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector(".mobile-facets__list");
    const sourceFacetsList = source.querySelector(".mobile-facets__list");

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  }

  static getSections() {
    const productGrid = document.getElementById("product-grid");
    return productGrid ? [{ section: productGrid.dataset.id }] : [];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event, signal = null) {
    FacetFiltersForm.renderPage(searchParams, event, true, signal);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll(
      "facet-filters-form form"
    );
    if (event.target.classList.contains("mobile-facets__checkbox")) {
      const searchParams = this.createSearchParams(
        event.target.closest("form")
      );
      this.abortController.abort();
      this.abortController = new AbortController();
      this.onSubmitForm(searchParams, event, this.abortController.signal);
    } else {
      const forms = [];
      const isMobile =
        event.target.closest("form").id === "FacetFiltersFormMobile";

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (
            form.id === "FacetSortForm" ||
            form.id === "FacetFiltersForm" ||
            form.id === "FacetSortDrawerForm"
          ) {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === "FacetFiltersFormMobile") {
          forms.push(this.createSearchParams(form));
        }
      });
      this.abortController.abort();
      this.abortController = new AbortController();
      this.onSubmitForm(forms.join("&"), event, this.abortController.signal);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") === -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1
          );
    this.abortController.abort();
    this.abortController = new AbortController();
    FacetFiltersForm.renderPage(url, event, true, this.abortController.signal);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.abortController = new AbortController();
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) => {
      element.addEventListener("change", this.onRangeChange.bind(this));
      element.addEventListener("keydown", this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern =
      /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll("input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute("data-max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("data-min", minInput.value);
    if (minInput.value === "") maxInput.setAttribute("data-min", 0);
    if (maxInput.value === "")
      minInput.setAttribute("data-max", maxInput.getAttribute("data-max"));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("data-min"));
    const max = Number(input.getAttribute("data-max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);
